import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { BadgeModule } from 'primeng/badge';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { AuthService } from '@/app/auth/auth.service';
import { LayoutService } from './service/layout.service';
import { isAdminPath, visibleAdminGroups } from '@/app/pages/admin/admin-nav';

// New (experimental) navigation. Diverges from the classic sidebar: the Core
// group is replaced by a single ungrouped "Worklist" item (no header), followed
// by the "Your site" entity group and the rest. Shares no code with the classic
// sidebar so it can be deleted cleanly once we pick a winner.
interface NavGroup {
    key: string;
    label: string | null; // null → rendered without a group header (ungrouped).
}

export interface NavItem {
    label: string;
    icon: string;
    routerLink: string;
    group: string; // matches NavGroup.key
    badgeKey?: 'validation' | 'approvals';
    permission?: string;
    /** When false, the item is hidden (feature-flagged nav entry). Absent = always shown. */
    enabled?: boolean;
}

// Icons follow the design-handoff PrimeIcons mapping — every leaf item resolves
// to a unique glyph (a duplicate icon carries no recognition value).
// Every item is permission-gated (view-level). Screen-view perms are granted broadly to working
// roles in the seed; admin/config screens (Site Scope, Integrations, Adapters, Requests/Triage,
// Tag, Workflows, Access Management, AI Gateway) are gated to the roles that actually use them.
const NAV_ITEMS: readonly NavItem[] = [
    { label: 'Dashboard', icon: 'pi pi-th-large', routerLink: '/dashboard', group: 'top', permission: 'dashboard:view' },
    // Temporary label — revisit. Underlying route/component (/worklist) unchanged.
    { label: 'Work Queue', icon: 'pi pi-check-square', routerLink: '/worklist', group: 'top', permission: 'worklist:view' },
    { label: 'Quick View', icon: 'pi pi-flag', routerLink: '/triage', group: 'top', permission: 'quick-view:view' },
    { label: 'Requests', icon: 'pi pi-inbox', routerLink: '/requests', group: 'top', permission: 'request:manage' },

    { label: 'Sources', icon: 'pi pi-database', routerLink: '/sources', group: 'Clinical content', permission: 'source:view' },
    { label: 'Treatment Protocols', icon: 'pi pi-sitemap', routerLink: '/treatment-protocols', group: 'Clinical content', permission: 'protocol:view' },
    { label: 'Regimens', icon: 'pi pi-list', routerLink: '/regimens', group: 'Clinical content', permission: 'regimen:view' },
    { label: 'Order sets', icon: 'pi pi-file-edit', routerLink: '/order-sets', group: 'Clinical content', permission: 'orderset:view' },
    { label: 'Governance', icon: 'pi pi-verified', routerLink: '/governance', group: 'Clinical content', permission: 'request:manage' }
    // Administration tools live behind the single entry below (see pages/admin/admin-nav.ts → /admin hub).
] as const;

const NAV_GROUPS: readonly NavGroup[] = [
    { key: 'top', label: 'Workspace' },
    { key: 'Clinical content', label: 'Clinical content' }
] as const;

@Component({
    selector: 'app-sidebar-new',
    standalone: true,
    imports: [CommonModule, RouterModule, BadgeModule, RippleModule, TooltipModule],
    templateUrl: './app-sidebar-new.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: `
        /* Read-only site context (handoff §7): plain overline + value, no card
           the [data-site-switcher] hook makes it easy to wire a dropdown later.
           The menu itself uses the shared/classic layout styling (no overrides). */
        .site-context {
            margin: 14px 14px 12px;
            padding: 9px 12px;
            background: var(--surface-ground);
            border: 1px solid var(--surface-border);
            border-radius: var(--border-radius, 8px);
        }
        .site-context-label {
            font-size: 10.5px;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            color: var(--text-color-secondary);
        }
        .site-context-name {
            margin-top: 2px;
            font-size: 13px;
            font-weight: 400;
            color: var(--text-color);
        }

        /* Administration renders as a group header (like Clinical content) that links to the /admin hub. */
        .cb-sidebar-new .admin-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            text-decoration: none;
            cursor: pointer;
            border-radius: var(--border-radius, 8px);
        }
        .cb-sidebar-new .admin-header:hover {
            background: var(--surface-hover);
        }
        .cb-sidebar-new .admin-header.active {
            color: var(--primary-color) !important;
        }

        /* Keep labels on one line and clip horizontally so the width animation (expand/collapse) reveals them
           cleanly instead of wrapping or jumping. */
        .cb-sidebar-new {
            overflow-x: hidden;
        }
        .cb-sidebar-new .layout-menuitem-text {
            white-space: nowrap;
        }

        /* Collapsed = icon-only rail: hide the site context, group headers, labels and badges, and centre icons.
           The sidebar width + content margin are set globally (_responsive.scss, .layout-nav-new.layout-static-inactive). */
        .cb-sidebar-new.rail .site-context-mini {
            /* Same 50px height as the expanded site box; initials centred. */
            box-sizing: border-box;
            height: 50px;
            margin: 14px 8px 12px;
            padding: 0;
            display: grid;
            place-items: center;
            font-size: 13px;
            font-weight: 600;
            letter-spacing: 0.04em;
            color: var(--text-color);
            cursor: default;
        }
        .cb-sidebar-new.rail .layout-menuitem-root-text {
            display: none;
        }
        .cb-sidebar-new.rail .layout-menuitem-text {
            display: none;
        }
        .cb-sidebar-new.rail .ml-auto {
            display: none;
        }
        .cb-sidebar-new.rail .layout-menu a {
            justify-content: center;
            padding-left: 0;
            padding-right: 0;
        }
        .cb-sidebar-new.rail .layout-menuitem-icon {
            margin-right: 0;
            /* Match the Drawer menu mode's rail icon size. */
            font-size: 1.25rem;
            width: auto;
        }
        /* Group headers are hidden in the rail, so mark group boundaries (Clinical content, Administration)
           with a thin divider between adjacent groups. */
        .cb-sidebar-new.rail .layout-root-menuitem + .layout-root-menuitem {
            border-top: 1px solid var(--surface-border);
            margin-top: 8px;
            padding-top: 8px;
        }
    `
})
export class AppSidebarNewComponent {
    private readonly auth = inject(AuthService);
    private readonly layout = inject(LayoutService);

    /** True when the desktop static sidebar is collapsed — drives the icon-only rail + hover tooltips. */
    readonly collapsed = computed(() => {
        const cfg = this.layout.layoutConfig();
        return cfg.menuMode === 'static' && this.layout.layoutState().staticMenuDesktopInactive;
    });

    /** Current tenant's site name; falls back to "Your site" when unavailable. */
    readonly siteName = computed(() => this.auth.currentUser()?.tenantName?.trim() || 'Your site');
    /** Up to two initials for the collapsed rail (e.g. "Cyan Blue Demo" → "CB"). */
    readonly siteInitials = computed(() =>
        this.siteName()
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((w) => w[0].toUpperCase())
            .join('')
    );

    readonly validationBadge = input<number | null | undefined>(undefined);
    readonly approvalsBadge = input<number | null | undefined>(undefined);

    readonly groups = NAV_GROUPS;

    private readonly router = inject(Router);
    private readonly url = toSignal(
        this.router.events.pipe(
            filter((e) => e instanceof NavigationEnd),
            map(() => this.router.url)
        ),
        { initialValue: this.router.url }
    );
    /** The single Administration entry: shown when the user can open at least one admin tool. */
    readonly showAdmin = computed(() => visibleAdminGroups((p) => this.auth.hasAnyPermission(p)).length > 0);
    /** Highlighted on the hub and on every admin tool page (exact-path routerLinkActive can't express that). */
    readonly adminActive = computed(() => isAdminPath(this.url()));

    readonly itemsByGroup = computed(() => {
        const out = new Map<string, NavItem[]>();
        for (const group of NAV_GROUPS) out.set(group.key, []);
        for (const item of NAV_ITEMS) {
            if (item.enabled === false) continue; // feature-flagged off
            if (item.permission && !this.auth.hasPermission(item.permission)) continue;
            out.get(item.group)!.push(item);
        }
        return out;
    });

    badgeFor(item: NavItem): number | null | undefined {
        if (item.badgeKey === 'validation') return this.validationBadge();
        if (item.badgeKey === 'approvals') return this.approvalsBadge();
        return undefined;
    }
}
